/* LeadModal.jsx — Captura nombre + contacto antes de mostrar la cotización.
 * Props: { open, onClose, onSubmit(leadData) }
 *   onSubmit recibe { nombre, contacto } y puede ser async (muestra spinner).
 * Reutiliza las clases CSS del hero: .modal-scrim, .modal, .cz-form, .cgo.wide
 */
import { useState, useEffect, useRef } from 'react';
import { Ico } from '../../core/heroCore';
import { telA10Digitos } from '../../config';

export default function LeadModal({ open, onClose, onSubmit }) {
  const [nombre, setNombre] = useState('');
  const [contacto, setContacto] = useState('');
  /* Pre-marcada a propósito: el camino de un solo clic es el que queremos, y
   * así el mensaje sale al desbloquear sin pedirle un segundo botón. Pero es
   * una casilla REAL y desmarcable, no un checkbox decorativo: es la evidencia
   * de consentimiento que Meta pide para plantillas de marketing, y lo que
   * evita que un lead molesto reporte spam al MISMO número con el que cobramos
   * (un reporte pega en el quality rating de todo el WABA). */
  const [porWhats, setPorWhats] = useState(true);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 60);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  /* 🔴 SOLO WhatsApp (decisión 17-ago-2026). Antes se aceptaba correo O teléfono,
   * y el lead que ponía correo quedaba sin canal para el flujo de WhatsApp: la
   * plantilla de Cloud API que arranca la conversación necesita un número.
   * La normalización vive en config.js porque el ContinueModal también la usa
   * (para reintentar con otro número): son 10 dígitos y nada más. */
  const tel10 = telA10Digitos(contacto);
  const contactoOk = tel10 !== null;
  const canSubmit = nombre.trim().length >= 2 && contactoOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    await onSubmit({ nombre: nombre.trim(), contacto: tel10, porWhats });
    setBusy(false);
  };

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="modal-x" onClick={onClose} aria-label="Cerrar">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18"/>
          </svg>
        </button>

        <div className="modal-head">
          <div className="modal-ic" style={{ background: 'rgba(0,186,110,.12)', color: 'var(--pei-green-true)' }}>
            <Ico.Check />
          </div>
          <h3>¡Tu cotización está lista!</h3>
          <p>Antes de verla, déjanos tu nombre y tu WhatsApp. Te escribimos ahí si tienes dudas.</p>
        </div>

        <form className="cz-form" style={{ marginTop: '20px' }} onSubmit={handleSubmit}>
          <label>
            Tu nombre
            <input
              ref={inputRef}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. María López"
              autoComplete="name"
            />
          </label>
          <label>
            Tu WhatsApp
            <input
              value={contacto}
              onChange={(e) => setContacto(e.target.value)}
              placeholder="55 1234 5678"
              autoComplete="tel"
              inputMode="tel"
              className={contacto && !contactoOk ? 'cz-invalid' : ''}
            />
            {contacto && !contactoOk && (
              <span className="cz-hint">
                {contacto.includes('@')
                  ? 'Aquí va tu WhatsApp, no el correo: 10 dígitos.'
                  : 'Pon los 10 dígitos de tu celular con WhatsApp.'}
              </span>
            )}
          </label>
          <button type="submit" className="cgo wide" style={{ marginTop: '4px' }} disabled={!canSubmit || busy}>
            {busy ? 'Un momento…' : 'Ver mi cotización'} {!busy && <Ico.Arrow />}
          </button>
        </form>

        <div className="modal-foot" style={{ marginTop: '16px', flexWrap: 'wrap', rowGap: 5 }}>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="5" y="11" width="14" height="9" rx="2"/>
            <path d="M8 11V8a4 4 0 0 1 8 0v3"/>
          </svg>
          Tus datos están protegidos. Consulta nuestro{' '}
          <a href="/aviso-de-privacidad">aviso de privacidad</a>.
          {/* Va aquí, en la letra chica, y no arriba del botón: no debe competir
              con el CTA. Pero sigue siendo una casilla REAL y desmarcable —
              esconder el consentimiento es justo lo que no se puede hacer, es la
              evidencia que Meta pide para plantillas de marketing. */}
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
            marginLeft: 4, fontSize: '10.5px', color: '#A8B3C2', whiteSpace: 'nowrap',
          }}>
            <input
              type="checkbox"
              checked={porWhats}
              onChange={(e) => setPorWhats(e.target.checked)}
              style={{ width: 11, height: 11, flexShrink: 0, accentColor: '#00BA6E', cursor: 'pointer' }}
            />
            Mándame la cotización por WhatsApp
          </label>
        </div>
      </div>
    </div>
  );
}
